<template>
  <div class="flex flex-col items-center bg-gray-100 dark:bg-dark-bg min-h-screen text-gray-800 dark:text-gray-200">
    <!-- Sidebar -->
    <AppSidebar />

    <div v-if="message"
         :class="messageType"
         class="mt-24 fixed top-5 left-1/2 transform -translate-x-1/2 p-4 rounded-lg text-center shadow-lg bg-white dark:bg-dark-box-bg dark:text-dark-text dark:border dark:border-dark-border transition-all duration-500"
         style="width: 90%; max-width: 1200px; z-index: 3000;">
      {{ message }}
      <div class="z-5000 absolute bottom-0 left-0 h-1"
           :style="{ width: progress + '%', backgroundColor: messageType.includes('green') ? '#16a34a' : '#b91c1c', borderRadius: '8px' }">
      </div>
    </div>

    <!-- Main Content Wrapper -->
    <div class="w-full max-w-4xl p-6 space-y-8">
      <!-- Header -->
      <div class="text-center">
        <h1 class="text-3xl font-bold">Panel Kierowcy</h1>
      </div>

      <!-- Main Content Layout -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div class="relative bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700" v-if="currentOrders.length > 0">
          <!-- Przycisk zamykania -->
           <div
            v-for="(currentOrder) in currentOrders"
            :key="currentOrder.id"
            class="relative bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700 mb-4"
          >
          <button
              v-if="isOrderStarted"
              @click="openGateModal(currentOrder)"
              class="absolute top-2 right-2 bg-button-bg text-button-text p-2 rounded-full shadow-md transition-transform duration-200 ease-in-out hover:bg-button-hover dark:bg-dark-button-bg dark:hover:bg-dark-button-hover"
              aria-label="Zamknij"
            >
              <PlusIcon class="h-6 w-6 transition-transform duration-200 ease-in-out transform hover:rotate-45" />
            </button>

          <h2 class="text-xl font-semibold mb-4">Aktualne Zlecenie Data: {{ currentOrder.date }}</h2>
          <h2 class="text-xl font-semibold mb-4 inline">Twoje Notatki:</h2> <p class="inline">{{ currentOrder.notesDriver }}</p>
          <div v-if="currentOrder.passenger && currentOrder.passenger.length > 0" class="divide-y divide-gray-300 dark:divide-gray-700">
              <div
                v-for="(passenger, index) in getSortedPassengers(currentOrder.passenger)"
                :key="passenger.orderPassengerId || index"
                class="py-4"
              >
              <div class="mb-2">
                <p class="font-semibold">Pasażer #{{ index + 1 }}</p>
              </div>
              <div class="py-2">
                <p class="font-semibold">Skąd odbiera:</p>
                <div class="block">{{ passenger.pickupAddress }}</div>
              </div>
              <div class="py-2">
                <p class="font-semibold">Godzina odbioru:</p>
                <div class="block">{{ passenger.pickupTime }}</div>
              </div>
              <div class="py-2">
                <p class="font-semibold">Dokąd wiezie:</p>
                <div class="block">{{ passenger.dropoffAddress }}</div>
              </div>
              <div class="py-2">
                <p class="font-semibold">Dane osoby odbieranej:</p>
                <div class="block">{{ passenger.firstName }} {{ passenger.lastName }}</div>
              </div>

              <div class="py-2">
                <p class="font-semibold">Numer telefonu:</p>
                <a 
                  class="block text-blue-600"
                  @click.prevent="makeCall(passenger.phone)"
                >
                  {{ passenger.phone }}
                </a>
              </div>

              <div class="flex flex-col gap-2 mt-4">
                <button
                    v-if="currentOrder.started && !passenger.waitingEndTime"
                    @click="handlePassengerAction(currentOrder, passenger, index)"
                    class="w-full py-2 text-white rounded-lg shadow-md"
                    :class="{
                      'bg-teal-600 hover:bg-teal-700': !passengerStates[passenger.orderPassengerId],
                      'bg-red-600 hover:bg-red-700': passengerStates[passenger.orderPassengerId]
                    }"
                    :aria-disabled="isStartingOrder || !isOrderFullyStarted"
                    aria-label="Oczekiwanie lub zakończenie oczekiwania na pasażera"
                  >
                    {{ passengerStates[passenger.orderPassengerId] ? 'Zakończ oczekiwanie na pasażera #' + (index + 1) : 'Oczekiwanie na pasażera #' + (index + 1) }}
                </button>
                
                <button
                  v-if="currentOrder.started && !passenger.waitingDroppedOffTime && !passenger.droppedOff && passenger.waitingEndTime"
                  @click="handleDroppedOffWaitingAction(currentOrder, passenger, index)"
                  class="w-full py-2 text-white rounded-lg shadow-md"
                  :class="{
                    'bg-orange-500 hover:bg-orange-600': !waitingDroppedOffStates[passenger.orderPassengerId],
                  }"
                  aria-label="Oczekiwanie na wysadzenie pasażera"
                >
                  {{ 'Oczekiwanie na wysadzenie #' + (index + 1) }}
                </button>

                <button
                  v-else-if="currentOrder.started && !passenger.droppedOff && passenger.waitingDroppedOffTime !== null"
                  @click="dropPassenger(currentOrder, passenger, index)"
                  class="w-full py-2 text-white rounded-lg shadow-md bg-blue-600 hover:bg-blue-700"
                  aria-label="Oznacz pasażera jako odwiezionego"
                >
                  Odwieziony Pasażer
                </button>
              </div>
            </div>
          </div>
          <div v-else>
            <p>Brak aktualnych zleceń.</p>
          </div>
          <div class="flex justify-center gap-2 flex-wrap" v-if="currentOrder && currentOrder.navigationLink">
            <button
                v-if="currentOrder.startDateTime === null"
                @click="startOrder(currentOrder)"
                id="mapButtonStart"
                :data-location="currentOrder.navigationLink"
                class="mt-4 flex-1 min-w-[120px] max-w-[140px] py-2 bg-gray-600 text-white rounded-lg shadow-md hover:bg-teal-700 focus:outline-none focus:ring focus:ring-blue-300 text-sm md:text-base lg:text-lg transition-transform transform hover:scale-105"
                :disabled="isStartingOrder || isAnyOrderStarted"
            >
              <span v-if="isStartingOrder" class="flex items-center justify-center">
                <svg class="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Ładowanie...
              </span>
              <span>Start</span>
            </button>

            <button
                @click="navigateToRoute(currentOrder)"
                id="mapButton"
                :data-id="currentOrder.id"
                :data-location="currentOrder.navigationLink"
                class="mt-4 flex-1 min-w-[120px] max-w-[140px] py-2 bg-gray-600 text-white rounded-lg shadow-md hover:bg-teal-700 focus:outline-none focus:ring focus:ring-blue-300 text-sm md:text-base lg:text-lg transition-transform transform hover:scale-105"
              >
                Nawiguj Trasę
            </button>
            <button
                v-if="currentOrder.started && areAllPassengersDropped"
                @click="closeOrderBox(currentOrder)"
                class="mt-4 flex-1 min-w-[120px] max-w-[140px] py-2 bg-gray-600 text-white rounded-lg shadow-md hover:bg-teal-700 focus:outline-none focus:ring focus:ring-blue-300 text-sm md:text-base lg:text-lg transition-transform transform hover:scale-105"
                aria-label="Zakończ trasę po odwiezieniu wszystkich pasażerów"
            >
              Zakończ Trasę
            </button>
          </div>
          </div>
        </div>

        <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
          <h2 class="text-xl font-semibold mb-4 text-center">Lista Zamówień</h2>
          <ul class="space-y-6">
            <li
                v-for="(order, index) in orders"
                :key="index"
                @click="showOrderDetails(order)"
                class="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:shadow-md cursor-pointer flex flex-col items-center space-y-4"
            >
              <div class="text-xl font-bold text-gray-800 dark:text-gray-200">
                # {{ order.mpk }}
              </div>
              <div 
                  class="text-md font-medium"
                  :class="{ 
                      'text-green-500': getOrderDateStatus(order.date).includes('dzisiaj'),
                      'text-blue-500': getOrderDateStatus(order.date).includes('jutro')
                  }"
                  v-html="getOrderDateStatus(order.date)"
              >
              </div>
              <div class="flex justify-center gap-2 w-full flex-wrap">
                <button
                    @click.stop="acceptOrder(order)"
                    class="bg-green-500 text-white hover:bg-green-600 focus:ring-green-300 flex items-center justify-center space-x-2 px-2 py-1 text-sm md:text-base lg:text-lg rounded-md focus:outline-none focus:ring transition-transform transform hover:scale-105 w-full max-w-[140px]"
                >
                  <CheckIcon class="w-4 h-4 md:w-5 md:h-5" />
                  <span>Akceptuj</span>
                </button>
                <button
                    @click.stop="rejectOrder(order)"
                    class="bg-red-500 text-white hover:bg-red-600 focus:ring-red-300 flex items-center justify-center space-x-2 px-2 py-1 text-sm md:text-base lg:text-lg rounded-md focus:outline-none focus:ring transition-transform transform hover:scale-105 w-full max-w-[140px]"
                >
                  <XMarkIcon class="w-4 h-4 md:w-5 md:h-5" />
                  <span>Odrzuć</span>
                </button>
              </div>
            </li>
          </ul>
        </div>

        <OrderInformationModal
          v-if="orderModal" 
          :selectedOrder="orderModal" 
          :visible="isModalVisible"
          :userRole="user.role"
          :title="'Szczegóły Zamówienia #' + (orderModal?.mpk || '')"
          @close="closeModal"
        >
          <div class="space-y-4">
            <div v-if="orderModal?.passenger && orderModal.passenger.length > 0">
              <h2 class="text-lg font-semibold">Data: {{ orderModal.date }}</h2>
              <h3 class="text-lg font-semibold">Pasażerowie:</h3>
              <ul class="space-y-2">
                <li v-for="(passenger, index) in sortedPassengers" :key="index">
                  <p>#{{ index + 1 }}: {{ passenger.firstName }} {{ passenger.lastName }}</p>
                  <p>Skąd: {{ passenger.pickupAddress }}</p>
                  <p>Dokąd: {{ passenger.dropoffAddress }}</p>
                  <p>Godzina: {{ passenger.pickupTime || 'Brak Danych' }}</p>
                </li>
              </ul>
              <h3 class="text-lg font-semibold">Notatka:</h3>
              <p>{{ orderModal?.notesDriver || 'Brak Notatki' }}</p>
            </div>
          </div>
        </OrderInformationModal>
      </div>
    </div>
    <StatusChangeWidget></StatusChangeWidget>

    <DriverGates
      :is-open="isModalOpen"
      :editing-data="tollData"
      :currentOrder="currentOrder"
      @close="closeGateModal"
      @submit="handleTollCostSubmit"
    />
  </div>
</template>

<script>
import orderService from '@/services/orderService';
import AppSidebar from '../AppSidebar.vue';
import { CheckIcon, XMarkIcon, PlusIcon } from '@heroicons/vue/24/solid';
import { useAuthStore } from '@/stores/authStore';
import OrderInformationModal from '../orders/OrderInformationModal.vue';
import StatusChangeWidget from "@/components/StatusChangeWidget.vue";
import { useSessionStore } from '@/stores/useSessionStore';
import DriverGates from './DriverGates.vue';
import driversService from '@/services/driversService';

export default {
  name: 'DashboardPageDriver',
  components: {
    AppSidebar,
    CheckIcon,
    XMarkIcon,
    PlusIcon,
    OrderInformationModal,
    StatusChangeWidget,
    DriverGates
  },
  setup() {
    const authStore = useAuthStore();
    const sessionStore = useSessionStore();

    if (authStore.isAuthenticated) {
      authStore.checkExpiration();
    }

    return {
      user: authStore.user,
      sessionStore,
    };
  },
  data() {
    return {
      currentOrders: [],
      get currentOrder() {
        return this.currentOrders[0] || null;
      },
      orders: [],
      message: '',
      messageType: '',
      progress: 0,
      isModalVisible: false,
      orderModal: null,
      passengerStates: {},
      isStartingOrder: false,
      isOrderFullyStarted: false,
      isModalOpen: false,
      tollData: { tollCost: '', orderId: null },
      isProcessingTollSubmit: false,
      waitingDroppedOffStates: {}
    };
  },
  computed: {
    isAnyOrderStarted() {
        return this.currentOrders.some(order => order.started === true);
    },
    isOrderStarted() {
      return this.currentOrder?.started === true;
    },
    sortedPassengers() {
      return this.orderModal?.passenger
        ? [...this.orderModal.passenger]
            .map(p => ({
              ...p,
              pickupTime: p.pickupTime && p.pickupTime.trim() !== "" ? p.pickupTime : "Brak danych"
            }))
            .sort(this.sortPassengersByOrder)
        : [];
    },
    sortedCurrentOrderPassengers() {
      return this.currentOrder?.passenger
        ? [...this.currentOrder.passenger].sort((a, b) => {
            // 1️⃣ sortOrder ma najwyższy priorytet
            if (a.sortOrder != null && b.sortOrder != null) {
              if (a.sortOrder !== b.sortOrder) {
                return a.sortOrder - b.sortOrder;
              }
            } else if (a.sortOrder != null) {
              return -1; // a ma sortOrder, b nie → a wyżej
            } else if (b.sortOrder != null) {
              return 1; // b ma sortOrder, a nie → b wyżej
            }

            // 2️⃣ jeśli brak sortOrder → porównujemy po orderPassengerId
            if (a.orderPassengerId != null && b.orderPassengerId != null) {
              if (a.orderPassengerId !== b.orderPassengerId) {
                return a.orderPassengerId - b.orderPassengerId;
              }
            }

            // 3️⃣ fallback → pickupTime (alfabetycznie, jak było)
            const aTime = a.pickupTime || '';
            const bTime = b.pickupTime || '';
            return aTime.localeCompare(bTime);
          })
        : [];
    },
    areAllPassengersDropped() {
      if (!this.currentOrder?.passenger || !this.isOrderStarted) return false;
      return this.currentOrder.passenger.every(p => p.droppedOff);
    }
  },
  mounted() {
    this.refreshOrders();
    this.refreshCurrentOrders();
    // this.startRefreshing();
    this.refreshHeartBeat();
  },
  beforeUnmount() {
    if (this.sessionStore.intervalId) {
      clearInterval(this.sessionStore.intervalId);
      this.sessionStore.intervalId = null;
    }
  },
  methods: {
    getSortedPassengers(passengers) {
      // POPRAWKA: Weryfikacja, czy 'passengers' jest tablicą i czy nie jest pusty/null/undefined
      if (!Array.isArray(passengers) || passengers.length === 0) {
        return [];
      }
      
      return [...passengers].sort((a, b) => {
        // 1️⃣ sortOrder ma najwyższy priorytet
        if (a.sortOrder != null && b.sortOrder != null) {
          if (a.sortOrder !== b.sortOrder) {
            return a.sortOrder - b.sortOrder;
          }
        } else if (a.sortOrder != null) {
          return -1;
        } else if (b.sortOrder != null) {
          return 1;
        }

        // 2️⃣ jeśli brak sortOrder → porównujemy po orderPassengerId
        if (a.orderPassengerId != null && b.orderPassengerId != null) {
          if (a.orderPassengerId !== b.orderPassengerId) {
            return a.orderPassengerId - b.orderPassengerId;
          }
        }

        // 3️⃣ fallback → pickupTime
        const aTime = a.pickupTime || '';
        const bTime = b.pickupTime || '';
        return aTime.localeCompare(bTime);
      });
    },
    // startRefreshing() {
    //   this.sessionStore.startInterval(() => {
    //     try {

    //     } catch (error) {
    //       console.error('Error refreshing orders:', error);
    //     }
    //   }, 15000);
    // },
    sortPassengersByOrder(a, b) {
      // 1️⃣ sortOrder ma najwyższy priorytet (numer numeryczny)
      if (a.sortOrder != null && b.sortOrder != null) {
        if (a.sortOrder !== b.sortOrder) {
          return a.sortOrder - b.sortOrder;
        }
      } else if (a.sortOrder != null) {
        return -1; // a ma sortOrder, b nie → a wyżej
      } else if (b.sortOrder != null) {
        return 1; // b ma sortOrder, a nie → b wyżej
      }

      // 2️⃣ jeśli brak sortOrder → porównujemy po orderPassengerId (numer numeryczny)
      if (a.orderPassengerId != null && b.orderPassengerId != null) {
        if (a.orderPassengerId !== b.orderPassengerId) {
          return a.orderPassengerId - b.orderPassengerId;
        }
      }

      // 3️⃣ fallback → pickupTime (alfabetycznie/czasowo)
      const aTime = a.pickupTime || '';
      const bTime = b.pickupTime || '';
      return aTime.localeCompare(bTime);
    },
    getOrderDateStatus(orderDate) {
        if (!orderDate) return 'Brak Danych Daty';

        // Używamy natywnego obiektu Date do porównania
        const today = new Date();
        // Resetujemy czas dla dzisiaj do północy
        today.setHours(0, 0, 0, 0);

        const orderDay = new Date(orderDate);
        // Resetujemy czas dla daty zlecenia do północy
        orderDay.setHours(0, 0, 0, 0);

        // Obliczenie różnicy w dniach (w milisekundach)
        const diffTime = orderDay.getTime() - today.getTime();
        const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 0) {
            return 'To zlecenie jest na DZISIAJ';
        } else if (diffDays === 1) {
            return 'To zlecenie jest na JUTRO';
        } else if (diffDays < 0) {
            // Zlecenie z przeszłości (opcjonalnie)
            return `Zlecenie z dnia: ${orderDate}`;
        } else {
            // Zlecenie na inny dzień w przyszłości
            return `Zlecenie na dzień: ${orderDate}`;
        }
    },
    refreshHeartBeat() {
      this.sessionStore.startInterval(() => {
        try {
          this.heartBeat();
          this.refreshOrders();
          this.refreshCurrentOrders();
        } catch (error) {
          console.error('Error sending heartbeat:', error);
        }
      }, 15000); // Co 60 sekund
    },
    async dropPassenger(order, passenger, index) {
      try {
        console.log(order.id, passenger.orderPassengerId);
        await orderService.dropPassenger(order.id, passenger.orderPassengerId);
        this.showMessage(`Pasażer #${index + 1} (${passenger.firstName} ${passenger.lastName}) został oznaczony jako odwieziony`, 'success');
        await this.refreshCurrentOrders();
      } catch (error) {
        this.showMessage('Nie udało się oznaczyć pasażera jako odwiezionego', 'error');
        console.error(error);
      }
    },
    async handleDroppedOffWaitingAction(order, passenger, index) {
        try {            
            await orderService.waitingDropOffPassenger(order.id, passenger.orderPassengerId); 
            this.showMessage(`Zarejestrowano czas oczekiwania na odbiór pasażera #${index + 1}. Możesz teraz oznaczyć pasażera jako odwiezionego.`, 'success');
            await this.refreshCurrentOrders();
        } catch (error) {
            this.showMessage('Nie udało się zarejestrować czasu oczekiwania na odbiór', 'error');
            console.error(error);
        }
    },
    async handlePassengerAction(order, passenger, index) {
    console.log(passenger);
      if (!this.passengerStates[passenger.orderPassengerId]) {
        try {
          await orderService.startPassenger(order.id, passenger.orderPassengerId);
          this.passengerStates[passenger.orderPassengerId] = true;
          this.showMessage(`Rozpoczęto oczekiwanie na pasażera #${index + 1}: ${passenger.firstName} ${passenger.lastName}`, 'success');
          await this.refreshCurrentOrders();
        } catch (error) {
          this.showMessage('Nie udało się rozpocząć oczekiwania', 'error');
          console.error(error);
        }
      } else {
        try {
          await orderService.endPassenger(order.id, passenger.orderPassengerId);
          this.passengerStates[passenger.orderPassengerId] = false;
          this.showMessage(`Zakończono oczekiwanie na pasażera #${index + 1}: ${passenger.firstName} ${passenger.lastName}`, 'success');
          await this.refreshCurrentOrders();
        } catch (error) {
          this.showMessage('Nie udało się zakończyć oczekiwania', 'error');
          console.error(error);
        }
      }
    },
    makeCall(phoneNumber) {
      const url = `tel:${phoneNumber.replace(/\s/g, '')}`;
      // 💡 Użyj _system, aby wymusić otwarcie przez system operacyjny
      window.open(url, '_system');
    },
    showOrderDetails(order) {
      if (!order) {
        console.warn("showOrderDetails: order is null or undefined");
        return;
      }
      this.orderModal = order;
      this.isModalVisible = true;
    },
    closeModal() {
      this.orderModal = null;
      this.isModalVisible = false;
    },
    showMessage(msg, type) {
      this.message = msg;
      this.messageType =
          type === 'success'
              ? 'bg-green-500 text-light-text dark:text-dark-text'
              : 'bg-red-500 text-light-text dark:text-dark-text';
      this.progress = 0;

      const interval = 30;
      const duration = 3000;
      const step = 100 / (duration / interval);

      const progressInterval = setInterval(() => {
        this.progress += step;
        if (this.progress >= 100) {
          clearInterval(progressInterval);
          this.clearMessage();
        }
      }, interval);
    },
    clearMessage() {
      this.message = '';
      this.messageType = '';
      this.progress = 0;
    },
    navigateToRoute(order) {
      window.location.href = order.navigationLink;
    },
    async closeOrderBox(order) {
      try {
        await orderService.completeOrder(order.id, this.user.uid);
        this.showMessage(`Zamówienie #${order.id} zostało zakończone`, 'success');
        this.orders = this.orders.filter(o => o.id !== order.id);
        await this.refreshCurrentOrders();
      } catch (error) {
        this.showMessage('Nie udało się zakończyć zamówienia', 'error');
        console.error(error);
      }
    },
    async acceptOrder(order) {
      try {
        await orderService.acceptOrder(order.id, this.user.uid);
        this.showMessage(`Zaakceptowano zamówienie #${order.id}`, 'success');
        this.orders = this.orders.filter(o => o.id !== order.id);
        await this.refreshCurrentOrders();
      } catch (error) {
        this.showMessage('Nie udało się zaakceptować zamówienia', 'error');
        console.error(error);
      }
    },
async retryRefreshCurrentOrder(orderId, maxAttempts, delay) {
      let attempts = 0;
      while (attempts < maxAttempts) {
        try {
          const response = await orderService.fetchCurrentOrders(this.user.uid);
          const data = response.data;
          
          // 1. Normalizacja danych do tablicy
          const fetchedOrders = Array.isArray(data) ? data : (data ? [data] : []);
          
          // 2. Szukanie konkretnego zlecenia, które miało wystartować
          const startedOrder = fetchedOrders.find(
            o => o.id === orderId && o.started && o.orderStatus === 'W_TRAKCIE'
          );

          // 3. Sprawdzenie, czy znaleziono i aktualizacja stanu
          if (startedOrder) {
            // Aktualizuj pełną listę aktywnych zleceń (w tym to uruchomione)
            this.currentOrders = fetchedOrders; 
            
            // Zainicjuj passengerStates dla WSZYSTKICH zleceń w currentOrders (aby obsługa pasażerów działała w nowej strukturze)
            this.passengerStates = {};
            this.currentOrders.forEach(order => {
                if (order.passenger) {
                    order.passenger.forEach(passenger => {
                        this.passengerStates[passenger.orderPassengerId] = !!passenger.waitingStartTime && !passenger.waitingEndTime;
                    });
                }
            });
            
            this.isOrderFullyStarted = true; // Flaga wskazująca, że start się powiódł
            return;
          }
        } catch (error) {
          console.error('Błąd podczas odświeżania zlecenia:', error);
        }

        attempts++;
        if (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
      
      // Jeżeli osiągnięto maksymalną liczbę prób i zlecenie nie wystartowało
      this.showMessage('Nie udało się w pełni załadować danych zlecenia lub zlecenie nie zmieniło statusu na W_TRAKCIE.', 'error');
      this.isOrderFullyStarted = false;
      
      // Warto również odświeżyć listę dla czystości, nawet jeśli błąd
      await this.refreshCurrentOrders();
    },
    async startOrder(order) {
      this.isStartingOrder = true;
      this.isOrderFullyStarted = false;
      try {
        await orderService.startOrder(order.id, this.user.uid);
        this.showMessage(`Wystartowałeś zamówienie #${order.id}`, 'success');
        await this.retryRefreshCurrentOrder(order.id, 5, 1000);
      } catch (error) {
        this.showMessage(`Nie udało się wystartować zamówienia: ${error.message}`, 'error');
        console.error(error);
      } finally {
        this.isStartingOrder = false;
      }

      window.open(order.navigationLink, '_blank');
    },
    async rejectOrder(order) {
      try {
        await orderService.rejectOrder(order.id);
        this.showMessage(`Odrzucono zamówienie #${order.id}`, 'error');
        this.orders = this.orders.filter(o => o.id !== order.id);
      } catch (error) {
        this.showMessage('Nie udało się odrzucić zamówienia', 'error');
        console.error(error);
      }
    },
    async refreshOrders() {
      try {
        const response = await orderService.fetchOrders(this.user.uid);
        this.orders = response.data;
      } catch (error) {
        console.error('Nie udało się pobrać zamówień:', error);
      }
    },
    async heartBeat() {
      try {
        await driversService.heartBeat(this.user.uid);
      } catch (error) {
        console.error('Nie udało się wysłać sygnału heartbeat:', error);
      }
    },
    async refreshCurrentOrders() {
      try {
        const response = await orderService.fetchCurrentOrders(this.user.uid);
        const data = response.data;

        // jeśli backend zwraca pojedynczy obiekt
        if (data && !Array.isArray(data)) {
          this.currentOrders = [data];
        }
        // jeśli backend zwraca tablicę
        else if (Array.isArray(data)) {
          this.currentOrders = data;
        } else {
          this.currentOrders = [];
        }

        // zbuduj passengerStates dla każdego zlecenia
        this.passengerStates = {};
        this.currentOrders.forEach(order => {
          if (order.passenger) {
            order.passenger.forEach(passenger => {
              this.passengerStates[passenger.orderPassengerId] = !!passenger.waitingStartTime && !passenger.waitingEndTime;
            });
          }
        });

      } catch (error) {
        console.error('Nie udało się pobrać aktualnych zleceń:', error);
      }
    },
    // async refreshCurrentOrder() {
    //   try {
    //     const response = await orderService.fetchCurrentOrder(this.user.uid);
    //     this.currentOrder = response.data;
    //     if (this.currentOrder?.passenger) {
    //       this.passengerStates = {};
    //       this.currentOrder.passenger.forEach(passenger => {
    //         this.passengerStates[passenger.orderPassengerId] = !!passenger.waitingStartTime && !passenger.waitingEndTime;
    //       });
    //     }
    //     if (this.currentOrder?.started && this.currentOrder?.orderStatus === 'W_TRAKCIE') {
    //       this.isOrderFullyStarted = true;
    //     } else {
    //       this.isOrderFullyStarted = false;
    //     }
    //   } catch (error) {
    //     console.error('Nie udało się pobrać aktualnego zamówienia:', error);
    //   }
    // },
    async startPassenger(order, passenger, index) {
      try {
        await orderService.startPassenger(order.id, passenger.orderPassengerId);
        this.passengerStates[passenger.orderPassengerId] = true;
        this.showMessage(`Rozpoczęto oczekiwanie na pasażera #${index + 1}: ${passenger.firstName} ${passenger.lastName}`, 'success');
        await this.refreshCurrentOrders();
      } catch (error) {
        this.showMessage('Nie udało się rozpocząć obsługi pasażera', 'error');
        console.error(error);
      }
    },
    async endPassenger(order, passenger, index) {
      try {
        await orderService.endPassenger(order.id, passenger.orderPassengerId);
        this.passengerStates[passenger.orderPassengerId] = false;
        this.showMessage(`Zakończono oczekiwanie na pasażera #${index + 1}: ${passenger.firstName} ${passenger.lastName}`, 'success');
        await this.refreshCurrentOrders();
      } catch (error) {
        this.showMessage('Nie udało się zakończyć obsługi pasażera', 'error');
        console.error(error);
      }
    },
    areAllPassengersReady(order) {
      return order.passenger.every((passenger) => this.passengerStates[passenger.orderPassengerId] === true);
    },
    openGateModal(order) {
      this.tollData = { tollCost: order.driverGates || '', orderId: order.id };
      this.isModalOpen = true;
    },
    closeGateModal() {
      this.isModalOpen = false;
      this.tollData = { tollCost: '', orderId: null };
    },
    async handleTollCostSubmit(data) {
      if (this.isProcessingTollSubmit) return;
      this.isProcessingTollSubmit = true;
      try {
        const payload = {
          driverGates: data.price
        }
        await orderService.updateDriverGate(this.currentOrder.id, payload);
          this.isModalOpen = false;
          this.showMessage(`Koszt bramek dla zlecenia #${this.currentOrder.id} został zaktualizowany`, 'success');
          await this.refreshCurrentOrders(); // Refresh to reflect updated toll cost
      } catch (error) {
        this.showMessage('Wystąpił błąd podczas aktualizacji kosztu bramek. Spróbuj ponownie.', 'error');
        console.error(error);
      } finally {
        this.isProcessingTollSubmit = false;
      }
    }
  },
};
</script>